import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Camera,
  FileText,
  Download,
  Settings,
  Menu,
  X,
  Send,
  FileUp,
  Fuel,
  Car,
  LogOut,
  FileSpreadsheet,
  Landmark,
  CalendarDays,
  GitCompare,
  Calculator,
  Sparkles,
  ChevronDown,
  Import,
  Wrench,
  FileOutput
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface DropdownGroup {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { logout, isCPA, userRole } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Primary nav items (always visible)
  const primaryItems: NavItem[] = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/expenses', label: 'Expenses', icon: FileSpreadsheet },
  ];

  // Grouped nav items for dropdowns - filtered based on role
  const ownerOnlyGroups: DropdownGroup[] = [
    {
      label: 'Capture',
      icon: Camera,
      items: [
        { path: '/scanner', label: 'Scan Receipt', icon: Camera },
        { path: '/smart-upload', label: 'OCR Upload', icon: Sparkles },
        { path: '/upload', label: 'Manual Upload', icon: FileUp },
      ]
    },
    {
      label: 'Import',
      icon: Import,
      items: [
        { path: '/import', label: 'Settlements', icon: FileText },
        { path: '/import-fuel', label: 'Fuel (Comdata)', icon: Fuel },
        { path: '/import-travel', label: 'Travel', icon: Car },
        { path: '/import-bank', label: 'Bank Statement', icon: Landmark },
      ]
    },
    {
      label: 'Tools',
      icon: Wrench,
      items: [
        { path: '/trips', label: 'Trip Calendar', icon: CalendarDays },
        { path: '/matcher', label: 'Transaction Match', icon: GitCompare },
        { path: '/tax', label: 'Tax Summary', icon: Calculator },
        { path: '/bol-send', label: 'BOL Send', icon: Send },
      ]
    },
  ];

  // Export group - available to both, but Settings only for owner
  const exportGroup: DropdownGroup = {
    label: 'Export',
    icon: FileOutput,
    items: isCPA 
      ? [
          { path: '/reports', label: 'Reports', icon: FileSpreadsheet },
          { path: '/export', label: 'Download PDF', icon: Download },
        ]
      : [
          { path: '/reports', label: 'Reports', icon: FileSpreadsheet },
          { path: '/export', label: 'Download PDF', icon: Download },
          { path: '/settings', label: 'Settings', icon: Settings },
        ]
  };

  // CPA gets Tax Summary too (read-only)
  const cpaToolsGroup: DropdownGroup = {
    label: 'Tools',
    icon: Wrench,
    items: [
      { path: '/tax', label: 'Tax Summary', icon: Calculator },
    ]
  };

  // Build dropdown groups based on role
  const dropdownGroups: DropdownGroup[] = isCPA 
    ? [cpaToolsGroup, exportGroup]
    : [...ownerOnlyGroups, exportGroup];

  // All items flat for mobile menu
  const allNavItems = [
    ...primaryItems,
    ...dropdownGroups.flatMap(g => g.items)
  ];

  // Check if any item in a dropdown is active
  const isGroupActive = (group: DropdownGroup) => 
    group.items.some(item => location.pathname === item.path);

  return (
    <div className="min-h-screen bg-gradient-to-br from-shuttle-dark via-shuttle-primary to-shuttle-light">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-shuttle-sand to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-xl">🧾</span>
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-semibold text-white">
                  Tax Tracker
                </span>
                <span className="text-xs text-shuttle-sky block -mt-1">
                  Expense Manager
                </span>
              </div>
            </Link>
            
            {/* CPA Badge */}
            {isCPA && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 border border-purple-400/30 rounded-full">
                <span className="text-purple-300 text-xs font-semibold uppercase tracking-wider">
                  👔 CPA View
                </span>
                <span className="text-purple-400/60 text-xs">(Read Only)</span>
              </div>
            )}

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center space-x-1" ref={dropdownRef}>
              {/* Primary Items */}
              {primaryItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    location.pathname === path
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </Link>
              ))}

              {/* Dropdown Groups */}
              {dropdownGroups.map((group) => (
                <div key={group.label} className="relative">
                  <button
                    onClick={() => setOpenDropdown(openDropdown === group.label ? null : group.label)}
                    className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isGroupActive(group) || openDropdown === group.label
                        ? 'bg-white/20 text-white'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <group.icon className="w-4 h-4" />
                    <span>{group.label}</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === group.label ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {openDropdown === group.label && (
                    <div className="absolute top-full left-0 mt-1 w-48 py-1 bg-slate-800/95 backdrop-blur-md border border-white/20 rounded-xl shadow-xl animate-fade-in z-50">
                      {group.items.map(({ path, label, icon: Icon }) => (
                        <Link
                          key={path}
                          to={path}
                          onClick={() => setOpenDropdown(null)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm transition-all ${
                            location.pathname === path
                              ? 'bg-white/15 text-white'
                              : 'text-white/70 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Logout */}
              <button
                onClick={logout}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-white/70 hover:bg-red-500/20 hover:text-red-400 transition-all ml-2"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/20 bg-shuttle-dark/95 backdrop-blur-md max-h-[80vh] overflow-y-auto">
            <nav className="px-4 py-2 space-y-1">
              {/* Primary items first */}
              {primaryItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    location.pathname === path
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{label}</span>
                </Link>
              ))}

              {/* Groups */}
              {dropdownGroups.map((group) => (
                <div key={group.label} className="pt-2 border-t border-white/10">
                  <div className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white/40 uppercase tracking-wider">
                    <group.icon className="w-3.5 h-3.5" />
                    {group.label}
                  </div>
                  {group.items.map(({ path, label, icon: Icon }) => (
                    <Link
                      key={path}
                      to={path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ml-2 ${
                        location.pathname === path
                          ? 'bg-white/20 text-white'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{label}</span>
                    </Link>
                  ))}
                </div>
              ))}

              {/* Logout */}
              <div className="pt-2 border-t border-white/10">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium text-white/70 hover:bg-red-500/20 hover:text-red-400 transition-all"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white/5 border-t border-white/10 py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-white/50 text-sm">
          Tax Expense Tracker • Built for the road 🚛
        </div>
      </footer>
    </div>
  );
}
